/**
 * Closing a logged ticket from the day view (v0.9.0's P3 UI; ADR 0182): the
 * popup's **Close ticket**, and the small **Close** on a dashed card of time
 * logged straight in Autotask (ADR 0183). The owner, 2026-10-20: "there also
 * was never a way to close a ticket that was logged, but not closed."
 *
 * - It asks first, in place: the ticket, where writes go, its status in
 *   Autotask becoming Complete, and as whom it's written.
 * - Then the close is queued (`POST /api/v1/tickets/{id}/close`) and
 *   followed (`GET /api/v1/ticket-actions/{id}`): "Reading the ticket…", then
 *   "Setting its status to Complete…" once the run has read it (`sent_at`).
 * - Done, the day refreshes, and the card moves to Closed with the green
 *   flash. Failed, Autotask's own words show, with **Try again** (the
 *   action's retry). A refusal (403, 404, 409) shows the API's words.
 *
 * The server decides everything: who may close it, as whom it's written, and
 * whether it can be closed now. Nothing here writes to Autotask by itself.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";

import { ApiError } from "../../api/client";
import type { TicketAction } from "../../api/types";
import { useSession } from "../../app/session-context";
import { useAnnounce } from "./announce";
import { useDayFacts } from "./dayContext";
import { isUnfinished, ticketActionQuery, useTicketAction } from "./ticketActions";
import { type TicketView, canClose, ticketName } from "./tickets";

/** Autotask's own words for a failed close, or why it failed (section 10.6). */
function failureWords(action: TicketAction): string | null {
  const words = action.autotask_errors ?? [];
  return words.length > 0 ? words.join(" ") : action.error;
}

type Phase =
  | { kind: "idle" }
  | { kind: "closing"; sent: boolean }
  | { kind: "failed"; words: string | null }
  | { kind: "done" };

/**
 * One ticket's close, as this view sees it: asking, following and trying
 * again. Its latest close is this view's own, or the day's (a ticket closing,
 * or whose close failed, names it), so after a reload it's still followed,
 * and tried again with its retry.
 */
function useTicketClose(ticket: TicketView) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const announce = useAnnounce();
  const name = ticketName(ticket);
  // The close asked for or tried again here, and how many times.
  const [mine, setMine] = useState<{ id: string; round: number } | null>(null);
  const actionId = mine?.id ?? ticket.closeActionId;
  const action = useTicketAction(actionId);
  // Made each time the tech asks: a second click on the same Close sends the
  // same key, and gets the same close (section 10.5, rule 4).
  const [key, setKey] = useState(() => crypto.randomUUID());
  const settle = (made: TicketAction) => {
    queryClient.setQueryData(ticketActionQuery(made.id), made);
    setMine((last) => ({ id: made.id, round: (last?.round ?? 0) + 1 }));
    void queryClient.invalidateQueries({ queryKey: ["day"] });
    void queryClient.invalidateQueries({ queryKey: ["tickets"] });
  };
  const onError = (error: Error) => {
    // Refused: the ticket may have changed since the day was read (closed
    // already, being closed): show it as it is now, with the refusal.
    if (error instanceof ApiError && error.status < 500) {
      void queryClient.invalidateQueries({ queryKey: ["day"] });
      void queryClient.invalidateQueries({ queryKey: ["tickets"] });
    }
  };
  const close = useMutation({
    mutationFn: (sent: string) => api.closeTicket(ticket.ticketId, sent),
    onSuccess: settle,
    onError,
  });
  const retry = useMutation({ mutationFn: (id: string) => api.retryTicketAction(id), onSuccess: settle, onError });

  const data = actionId === null ? undefined : action.data;
  // Asked for here: how it ended is said once, and the day shows it.
  const ended = mine !== null && data?.id === mine.id && !isUnfinished(data) ? data.state : null;
  const told = useRef<string | null>(null);
  useEffect(() => {
    const tag = mine === null || ended === null ? null : `${mine.id}:${String(mine.round)}:${ended}`;
    if (tag === null || told.current === tag) {
      return;
    }
    told.current = tag;
    announce(ended === "done" ? `Closed ticket ${name}.` : `Couldn't close ticket ${name}.`);
    void queryClient.invalidateQueries({ queryKey: ["day"] });
    void queryClient.invalidateQueries({ queryKey: ["tickets"] });
  }, [mine, ended, announce, name, queryClient]);

  let phase: Phase;
  if (close.isPending || retry.isPending) {
    phase = { kind: "closing", sent: false };
  } else if (data !== undefined && isUnfinished(data)) {
    phase = { kind: "closing", sent: data.sent_at !== null };
  } else if (data?.state === "failed" && ticket.state !== "closed") {
    phase = { kind: "failed", words: failureWords(data) };
  } else if (data?.state === "done") {
    phase = { kind: "done" };
  } else if (ticket.state === "closing") {
    phase = { kind: "closing", sent: false };
  } else if (ticket.state === "close_failed") {
    phase = { kind: "failed", words: null };
  } else {
    phase = { kind: "idle" };
  }

  return {
    name,
    phase,
    refusal: close.isError ? close.error.message : retry.isError ? `Not tried again: ${retry.error.message}` : null,
    busy: close.isPending || retry.isPending,
    /** A new confirm: a new close, with its own key. */
    ask: () => {
      setKey(crypto.randomUUID());
      close.reset();
      retry.reset();
    },
    send: () => {
      close.mutate(key);
    },
    /** A failed close again: its retry, or a new close where it isn't known here. */
    again: () => {
      if (actionId !== null && data?.state === "failed") {
        retry.mutate(actionId);
      } else {
        close.mutate(crypto.randomUUID());
      }
    },
  };
}

const cta =
  "h-11 cursor-pointer rounded-pill bg-cta-bg px-5 text-[14px] font-bold text-cta-fg disabled:cursor-not-allowed disabled:opacity-50";
const button =
  "h-10 cursor-pointer self-start rounded-pill border border-line bg-panel px-4 text-[13.5px] font-semibold hover:bg-panel-2 disabled:cursor-not-allowed disabled:opacity-50";
const alert = "text-[13px] text-status-needs-client-fg";

/** The steps of a close under way: the read, then the write (the mockup's `loggedHTML`). */
function Closing({ name, sent, statusLabel }: { name: string; sent: boolean; statusLabel: string | null }) {
  return (
    <section aria-label="Closing the ticket" className="flex flex-col gap-2 rounded-row bg-panel-2 px-3 py-2.5">
      <p role="status" className="text-[13.5px] font-semibold">
        {`Closing ticket ${name}…`}
      </p>
      <ul aria-label="Steps" className="flex flex-col gap-0.5 text-[13px]">
        {sent ? (
          <li className="flex gap-1.5">
            <span aria-hidden="true" className="text-status-logged-fg">
              ✓
            </span>
            <span>{statusLabel === null ? "Read the ticket" : `Read the ticket: ${statusLabel}`}</span>
          </li>
        ) : null}
        <li className="text-muted motion-safe:animate-pulse">
          {sent ? "Setting its status to Complete…" : "Reading the ticket…"}
        </li>
      </ul>
    </section>
  );
}

/**
 * The popup's **Close ticket**, for a logged block whose ticket is open, or
 * whose close failed (ADR 0182): asks first, then follows the close.
 */
export function CloseTicket({
  ticket,
  asking: askingFirst = false,
  where,
  onSent,
  beside,
}: {
  ticket: TicketView;
  /** It asks at once, its question focused: a card's "Close ticket…" (ADR 0194). */
  asking?: boolean | undefined;
  /** Where writes go here, as the Log form says it; null where they go everywhere. */
  where: string | null;
  /** The confirm or Try again is gone: focus goes back to the popup's heading (#247). */
  onSent?: (() => void) | undefined;
  /** The popup's More ⋯ (ADR 0194): beside Close ticket, or under what's shown in its place. */
  beside?: ReactNode;
}) {
  const { techName } = useDayFacts();
  const { me } = useSession();
  const close = useTicketClose(ticket);
  const [asking, setAsking] = useState(askingFirst);
  const opener = useRef<HTMLButtonElement>(null);
  const question = useRef<HTMLParagraphElement>(null);
  const backToOpener = useRef(false);
  useLayoutEffect(() => {
    if (asking) {
      question.current?.focus();
    } else if (backToOpener.current) {
      backToOpener.current = false;
      opener.current?.focus();
    }
  }, [asking]);

  const { phase, name } = close;
  const refusal =
    close.refusal === null ? null : (
      <p role="alert" className={alert}>
        {close.refusal}
      </p>
    );
  if (phase.kind === "done") {
    return <>{beside}</>;
  }
  if (phase.kind === "closing") {
    return (
      <>
        <Closing name={name} sent={phase.sent} statusLabel={ticket.statusLabel} />
        {beside}
      </>
    );
  }
  if (phase.kind === "failed") {
    return (
      <>
        <section aria-label="Closing the ticket" className="flex flex-col gap-2 rounded-row bg-panel-2 px-3 py-2.5">
          <p className="text-[13.5px] font-semibold text-status-needs-client-fg">{`Couldn't close ticket ${name}`}</p>
          <p role="alert" className={alert}>
            {phase.words ??
              // A tech can't open Health (#833).
              (me.role === "owner"
                ? "Autotask didn't take it. Health → Autotask shows why."
                : "Autotask didn't take it: try again, or ask an owner to look at it.")}
          </p>
          <button
            type="button"
            disabled={close.busy}
            onClick={() => {
              close.again();
              onSent?.();
            }}
            className={button}
          >
            Try again
          </button>
          {refusal}
        </section>
        {beside}
      </>
    );
  }
  if (!asking || !canClose(ticket.state)) {
    return (
      <>
        {canClose(ticket.state) ? (
          // The primary action, the popup's More ⋯ beside it (ADR 0194).
          <div className="relative flex flex-wrap items-center gap-2">
            <button
              ref={opener}
              type="button"
              onClick={() => {
                close.ask();
                setAsking(true);
              }}
              className={cta}
            >
              Close ticket
            </button>
            {beside}
          </div>
        ) : (
          beside
        )}
        {refusal}
      </>
    );
  }
  return (
    <>
      <div role="group" aria-label={`Close ticket ${name}`} className="flex flex-col gap-2.5 rounded-row bg-panel-2 p-3">
        <p ref={question} tabIndex={-1} className="text-[14px] font-[650] focus:outline-none">
          {`Close ticket ${name}?`}
        </p>
        {where === null ? null : (
          <p className="rounded-row border border-dashed border-line px-3 py-2 text-[12.5px] text-muted">{where}</p>
        )}
        <p className="flex flex-wrap items-center gap-2 text-[13px]">
          <span className="text-[12.5px] text-muted">Its status in Autotask</span>
          {ticket.statusLabel === null ? null : (
            <>
              <span className="rounded-pill border border-line bg-panel px-2.5 py-[3px] text-[12.5px] font-semibold">
                {ticket.statusLabel}
              </span>
              <span aria-hidden="true" className="text-muted">
                →
              </span>
              <span className="sr-only">becomes</span>
            </>
          )}
          <span className="rounded-pill border border-status-logged-fg bg-status-logged-bg px-2.5 py-[3px] text-[12.5px] font-semibold text-status-logged-fg">
            Complete
          </span>
        </p>
        <p className="text-[12.5px] leading-[1.45] text-muted">
          {`Written as ${techName ?? "the tech"}, who logged it. Axon Time reads the ticket first: if it's already Complete, nothing is written.`}
        </p>
        {refusal}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={close.busy}
            onClick={() => {
              close.send();
              setAsking(false);
              onSent?.();
            }}
            className={cta}
          >
            Close ticket
          </button>
          <button
            type="button"
            onClick={() => {
              backToOpener.current = true;
              setAsking(false);
            }}
            className="h-11 cursor-pointer rounded-pill border border-line bg-panel px-4 text-[13.5px] font-semibold hover:bg-panel-2"
          >
            Cancel
          </button>
        </div>
      </div>
      {beside}
    </>
  );
}

const small =
  "h-[34px] cursor-pointer rounded-pill border border-line bg-panel px-3.5 text-[13px] font-semibold text-text hover:bg-panel-2 disabled:cursor-not-allowed disabled:opacity-50";

/**
 * The small **Close** on a dashed card, time logged straight in Autotask
 * (the owner's decision 3, 2026-10-21; ADR 0183; the mockup's `entryHTML`):
 * the same close, asked inline. It's written as the tech who entered the
 * time; the server decides.
 */
export function EntryClose({ ticket }: { ticket: TicketView }) {
  const { techName } = useDayFacts();
  const close = useTicketClose(ticket);
  const [asking, setAsking] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const group = useRef<HTMLDivElement>(null);
  const backToOpener = useRef(false);
  useLayoutEffect(() => {
    if (asking) {
      group.current?.focus();
    } else if (backToOpener.current) {
      backToOpener.current = false;
      opener.current?.focus();
    }
  }, [asking]);

  const { phase, name } = close;
  const refusal =
    close.refusal === null ? null : (
      <p role="alert" className="basis-full text-[12.5px] text-status-needs-client-fg">
        {close.refusal}
      </p>
    );
  const row = "flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[12.5px]";
  if (phase.kind === "failed") {
    return (
      <div className={row}>
        {phase.words === null ? null : (
          <p role="alert" className="basis-full text-status-needs-client-fg">
            {phase.words}
          </p>
        )}
        <button
          type="button"
          aria-label={`Try again to close ticket ${name}`}
          disabled={close.busy}
          onClick={() => {
            close.again();
          }}
          className={small}
        >
          Try again
        </button>
        {refusal}
      </div>
    );
  }
  if (phase.kind !== "idle") {
    return null;
  }
  if (!asking || !canClose(ticket.state)) {
    return canClose(ticket.state) || refusal !== null ? (
      <div className={row}>
        {canClose(ticket.state) ? (
          <button
            ref={opener}
            type="button"
            aria-label={`Close ticket ${name}`}
            onClick={() => {
              close.ask();
              setAsking(true);
            }}
            className={small}
          >
            Close
          </button>
        ) : null}
        {refusal}
      </div>
    ) : null;
  }
  return (
    <div ref={group} role="group" tabIndex={-1} aria-label={`Close ticket ${name}`} className={`${row} focus:outline-none`}>
      <span>
        {`Set ${name} to `}
        <b>Complete</b>
        {` in Autotask${techName === null ? "" : `, as ${techName}`}?`}
      </span>
      <button
        type="button"
        disabled={close.busy}
        onClick={() => {
          close.send();
          setAsking(false);
        }}
        className="h-[34px] cursor-pointer rounded-pill bg-cta-bg px-3.5 text-[13px] font-bold text-cta-fg disabled:cursor-not-allowed disabled:opacity-50"
      >
        Close
      </button>
      <button
        type="button"
        onClick={() => {
          backToOpener.current = true;
          setAsking(false);
        }}
        className={small}
      >
        Cancel
      </button>
      {refusal}
    </div>
  );
}
